import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { colorLabel, type AppointmentType, type AppointmentTypeColor } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { toneClass } from './appointmentTypes';
import { ColorPicker } from '../../components/color/ColorPicker';
import './calendar.css';

/** Bereich „Terminarten“ der Einrichtung. Die Rechte prüft der Server, die Oberfläche blendet nur aus. */
export function AppointmentTypesPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('setup.write');
  const canDelete = can('setup.delete');

  const types = useQuery({ queryKey: ['appointment-types', 'manage'], queryFn: async () => (await api<{ data: AppointmentType[] }>('GET', '/appointment-types/manage')).data });
  const refresh = () => client.invalidateQueries({ queryKey: ['appointment-types'] });

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('#0f766e');
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string>('#0f766e');
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () => api('POST', '/appointment-types', { name: name.trim(), color }),
    onSuccess: () => {
      setName('');
      setCreateErrors({});
      refresh();
    },
    onError: (e) => setCreateErrors(e instanceof ApiError && e.status === 422 ? e.fields : { name: e instanceof ApiError ? e.message : 'Die Art konnte nicht angelegt werden.' }),
  });
  const update = useMutation({
    mutationFn: (id: string) => api('PATCH', `/appointment-types/${id}`, { name: editName.trim(), color: editColor }),
    onSuccess: () => {
      setEditing(null);
      setEditErrors({});
      refresh();
    },
    onError: (e) => setEditErrors(e instanceof ApiError && e.status === 422 ? e.fields : { name: e instanceof ApiError ? e.message : 'Die Art konnte nicht geändert werden.' }),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api('POST', '/appointment-types/order', { ids }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/appointment-types/${id}`),
    onSuccess: refresh,
  });

  const list = types.data ?? [];
  const move = (index: number, delta: -1 | 1) => {
    const ids = list.map((t) => t.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate(ids);
  };
  const startEdit = (t: AppointmentType) => {
    setEditing(t.id);
    setEditName(t.name);
    setEditColor(t.color);
    setEditErrors({});
  };
  const error = [reorder.error, remove.error, types.error].find((e) => e instanceof ApiError) as ApiError | undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setCreateErrors({});
    create.mutate();
  }

  return (
    <section className="card">
      <h2>Termin- und Aufgabenarten</h2>
      <p className="muted small">
        Welche Arten von Terminen Ihr Unternehmen hat, zum Beispiel „Meeting“, „Kundentermin“, „Workshop“ oder „Buchhaltung“. Beim Anlegen eines Termins oder einer Aufgabe (dort heißt das Feld „Aufgabentyp“) wählt man die Art aus, und der Kalender lässt
        sich danach filtern. Die Farbe zeigt im Kalender, welcher Art ein Eintrag ist.
      </p>

      {error && <div className="alert alert--error" role="alert">{error.message}</div>}

      {canWrite && (
        <form className="type-form" onSubmit={submit} noValidate>
          <label className="field">
            <span>Neue Art</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Zum Beispiel Workshop" maxLength={60} autoComplete="off" aria-invalid={!!createErrors.name} />
            {createErrors.name && <small className="field__error" role="alert">{createErrors.name}</small>}
          </label>
          <div className="field">
            <span>Farbe</span>
            <ColorPicker value={color} onChange={setColor} label="Farbe der neuen Art" />
          </div>
          <button className="btn btn--primary type-form__submit" type="submit" disabled={create.isPending || name.trim().length < 2}>
            {create.isPending ? 'Wird angelegt …' : 'Art anlegen'}
          </button>
        </form>
      )}

      <table className="table table--cards">
        <thead>
          <tr>
            <th>Art</th>
            <th>Farbe</th>
            <th>Verwendet</th>
            <th><span className="sr-only">Aktionen</span></th>
          </tr>
        </thead>
        <tbody>
          {types.isLoading && <tr><td colSpan={4} className="muted">Wird geladen …</td></tr>}
          {!types.isLoading && list.length === 0 && <tr><td colSpan={4} className="empty">Noch keine Terminarten angelegt. Termine funktionieren auch ohne Art.</td></tr>}
          {list.map((t, index) => {
            const isEditing = editing === t.id;
            return (
              <tr key={t.id}>
                <td>
                  {isEditing ? (
                    <>
                      <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={60} aria-label={`Bezeichnung von ${t.name}`} aria-invalid={!!editErrors.name} />
                      {editErrors.name && <small className="field__error" role="alert">{editErrors.name}</small>}
                    </>
                  ) : (
                    <span className="type-name"><i className={`type-swatch ${toneClass(t.color)}`} aria-hidden="true" />{t.name}</span>
                  )}
                </td>
                <td data-label="Farbe">
                  {isEditing ? (
                    <ColorPicker value={editColor} onChange={setEditColor} label={`Farbe von ${t.name}`} />
                  ) : (
                    <span className={`type-badge ${toneClass(t.color)}`}>
                      {colorLabel(t.color)}
                    </span>
                  )}
                </td>
                <td data-label="Verwendet">{t.usageCount}</td>
                <td className="cell-actions">
                  <div className="row-actions">
                    {canWrite && isEditing && (
                      <>
                        <button type="button" className="btn btn--primary btn--sm" disabled={update.isPending} onClick={() => update.mutate(t.id)}>Speichern</button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditing(null)}>Abbrechen</button>
                      </>
                    )}
                    {canWrite && !isEditing && (
                      <>
                        <button type="button" className="btn btn--secondary btn--sm" aria-label={`${t.name} nach oben`} disabled={index === 0 || reorder.isPending} onClick={() => move(index, -1)}>↑</button>
                        <button type="button" className="btn btn--secondary btn--sm" aria-label={`${t.name} nach unten`} disabled={index === list.length - 1 || reorder.isPending} onClick={() => move(index, 1)}>↓</button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => startEdit(t)}>Bearbeiten</button>
                      </>
                    )}
                    {canDelete && !isEditing && (
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Terminart „${t.name}“ wirklich löschen?`)) remove.mutate(t.id);
                        }}
                      >
                        Löschen
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted small">Eine Art, die in Terminen verwendet wird, lässt sich nicht löschen. Benennen Sie sie stattdessen um, oder ändern Sie zuerst die Art der Termine.</p>
    </section>
  );
}
